import React, { useState, useEffect } from 'react';
import { API_ENDPOINTS } from '../../config/api';
import './Referral.css';
import { authFetch } from '../../utils/authFetch';


const Referral = () => {
  const [email, setEmail] = useState('');
  const [message, setMessage] = useState('');
  const [activeTab, setActiveTab] = useState('invite');
  const [copied, setCopied] = useState(false);
  const [loading, setLoading] = useState(true);
  const [sendingInvite, setSendingInvite] = useState(false);
  const [notification, setNotification] = useState({ show: false, message: '', type: '' });

  const [referralData, setReferralData] = useState({
    code: '',
    link: '',
    totalReferrals: 0,
    creditsEarned: 0
  });

  const [stats, setStats] = useState({
    successfulReferrals: 0,
    pendingReferrals: 0,
    creditsEarned: 0,
    bonusSearches: 0
  });

  const [history, setHistory] = useState([]);

  const userId = localStorage.getItem('userEmail');

  const showNotification = (message, type = 'success') => {
    setNotification({ show: true, message, type });
    setTimeout(() => {
      setNotification({ show: false, message: '', type: '' });
    }, 3000);
  };

  useEffect(() => {
    fetchReferralData();
  }, []);

  const fetchReferralData = async () => {
    if (!userId) {
      showNotification('Please log in to access referral program', 'error');
      setLoading(false);
      return;
    }

    try {
      setLoading(true);

      // ✅ CORRECTED: GET request to /get_referral_code with user_id as query param
      const codeResponse = await authFetch(
        `${API_ENDPOINTS.GET_REFERRAL_CODE}?user_id=${encodeURIComponent(userId)}`
      );

      if (!codeResponse.ok) throw new Error('Failed to get referral code');
      const codeData = await codeResponse.json();
      
      const referralCode = codeData.referral_code;
      const referralLink = `${window.location.origin}/signup?ref=${referralCode}`;

      setReferralData({
        code: referralCode,
        link: referralLink,
        totalReferrals: 0,
        creditsEarned: 0
      });

      const statsResponse = await authFetch(
        `${API_ENDPOINTS.GET_REFERRAL_STATS}?user_id=${encodeURIComponent(userId)}`
      );

      if (statsResponse.ok) {
        const statsData = await statsResponse.json();
        
        setStats({
          successfulReferrals: statsData.successful_referrals || 0,
          pendingReferrals: statsData.pending_referrals || 0,
          creditsEarned: (statsData.successful_referrals || 0) * 10,
          bonusSearches: statsData.bonus_searches_awarded || 0
        });

        // ✅ CORRECTED: Fetch history from /get_referral_history
        const historyResponse = await authFetch(
          `${API_ENDPOINTS.GET_REFERRAL_HISTORY}?user_id=${encodeURIComponent(userId)}`
        );
        
        if (historyResponse.ok) {
          const historyData = await historyResponse.json();
          setHistory(historyData.referral_history || []);
        }
      }

    } catch (error) {
      showNotification('Failed to load referral data', 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleSendInvite = async (e) => {
    e.preventDefault();
    
    if (!email) {
      showNotification('Please enter an email address', 'error');
      return;
    }

    try {
      setSendingInvite(true);

      // Build request body - only include personal_message if it exists
      const requestBody = {
        user_id: userId,
        referee_email: email,
        source: 'email'
      };

      // Only add personal_message if user typed something
      if (message && message.trim()) {
        requestBody.personal_message = message.trim();
      }

      const response = await authFetch(API_ENDPOINTS.SEND_REFERRAL_INVITE, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(requestBody)
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || errorData.message || 'Failed to send invitation');
      }

      const result = await response.json();
      showNotification(`Invitation sent successfully to ${email}!`, 'success');
      setEmail('');
      setMessage('');
      
      await fetchReferralData();

    } catch (error) {
      showNotification(error.message || 'Failed to send invitation', 'error');
    } finally {
      setSendingInvite(false);
    }
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    setCopied(true);
    showNotification('Referral link copied to clipboard!', 'success');
    setTimeout(() => setCopied(false), 2000);
  };

  const shareViaEmail = () => {
    const subject = encodeURIComponent('Join me on JobQue - Get 50% OFF!');
    const body = encodeURIComponent(
      `I've been using JobQue to streamline my job search and thought you might like it too!\n\n` +
      `Sign up using my referral link to get 50% OFF your first month:\n${referralData.link}\n\n` +
      `Looking forward to having you on board!`
    );
    window.location.href = `mailto:?subject=${subject}&body=${body}`;
  };

  const shareViaSocial = (platform) => {
    const text = encodeURIComponent('Get 50% OFF when you join JobQue using my referral link!');
    const url = encodeURIComponent(referralData.link);
    
    const urls = {
      twitter: `https://twitter.com/intent/tweet?text=${text}&url=${url}`,
      facebook: `https://www.facebook.com/sharer/sharer.php?u=${url}`,
      linkedin: `https://www.linkedin.com/sharing/share-offsite/?url=${url}`
    };

    window.open(urls[platform], '_blank', 'width=600,height=400');
  };

  if (loading) {
    return (
      <div className="referral-container">
        <div className="loading-state">
          <div className="spinner"></div>
          <p>Loading referral data...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="referral-container">
      {notification.show && (
        <div className={`notification ${notification.type}`}>
          {notification.message}
        </div>
      )}

      <div className="referral-header">
        <h2>Refer & Earn</h2>
        <p>Invite friends and earn 10 bonus searches for each successful referral!</p>
      </div>

      <div className="stats-grid">
        <div className="stat-card">
          <div className="stat-content">
            <div className="stat-value">{stats.successfulReferrals}</div>
            <div className="stat-label">Successful Referrals</div>
          </div>
        </div>
        <div className="stat-card">
          <div className="stat-content">
            <div className="stat-value">{stats.pendingReferrals}</div>
            <div className="stat-label">Pending Invites</div>
          </div>
        </div>
        <div className="stat-card highlight">
          <div className="stat-content">
            <div className="stat-value">{stats.creditsEarned}</div>
            <div className="stat-label">Credits Earned</div>
          </div>
        </div>
        <div className="stat-card highlight">
          <div className="stat-content">
            <div className="stat-value">{stats.bonusSearches}</div>
            <div className="stat-label">Bonus Searches</div>
          </div>
        </div>
      </div>

      <div className="tab-navigation">
        <button 
          className={`tab-button ${activeTab === 'invite' ? 'active' : ''}`}
          onClick={() => setActiveTab('invite')}
        >
          Send Invite
        </button>
        <button 
          className={`tab-button ${activeTab === 'share' ? 'active' : ''}`}
          onClick={() => setActiveTab('share')}
        >
          Share Link
        </button>
        <button 
          className={`tab-button ${activeTab === 'history' ? 'active' : ''}`}
          onClick={() => setActiveTab('history')}
        >
          History
        </button>
      </div>

      <div className="tab-content">
        {activeTab === 'invite' && (
          <div className="invite-section">
            <h3>Send Personal Invitation</h3>
            <p className="section-description">
              Send a personalized invitation email to your friends
            </p>
            
            <form onSubmit={handleSendInvite} className="invite-form">
              <div className="form-group">
                <label htmlFor="email">Email Address *</label>
                <input
                  type="email"
                  id="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="friend@example.com"
                  required
                  disabled={sendingInvite}
                />
              </div>
              
              <div className="form-group">
                <label htmlFor="message">Personal Message (Optional)</label>
                <textarea
                  id="message"
                  value={message}
                  onChange={(e) => setMessage(e.target.value)}
                  placeholder="Add a personal message to your invitation..."
                  rows="4"
                  disabled={sendingInvite}
                />
              </div>

              <button type="submit" className="btn-primary" disabled={sendingInvite}>
                {sendingInvite ? 'Sending...' : 'Send Invitation'}
              </button>
            </form>

            <div className="benefits-box">
              <h4>What they get:</h4>
              <ul>
                <li>50% OFF on their first month subscription</li>
                <li>Instant access to all premium features</li>
                <li>Priority customer support</li>
              </ul>
              <h4 style={{ marginTop: '1rem' }}>What you get:</h4>
              <ul>
                <li>10 bonus searches added to your account</li>
                <li>Unlimited referrals - no cap!</li>
              </ul>
            </div>
          </div>
        )}

        {activeTab === 'share' && (
          <div className="share-section">
            <h3>Share Your Referral Link</h3>
            <p className="section-description">
              Copy and share your unique referral link
            </p>

            <div className="referral-code-box">
              <div className="code-display">
                <label>Your Referral Code</label>
                <div className="code-value">{referralData.code}</div>
              </div>
            </div>

            <div className="link-box">
              <input
                type="text"
                value={referralData.link}
                readOnly
                className="link-input"
              />
              <button
                onClick={() => copyToClipboard(referralData.link)}
                className="btn-copy"
              >
                {copied ? 'Copied!' : 'Copy'}
              </button>
            </div>

            <div className="share-buttons">
              <h4>Share via:</h4>
              <div className="social-buttons">
                <button onClick={shareViaEmail} className="share-btn email">
                  Email
                </button>
                <button onClick={() => shareViaSocial('twitter')} className="share-btn twitter">
                  Twitter
                </button>
                <button onClick={() => shareViaSocial('facebook')} className="share-btn facebook">
                  Facebook
                </button>
                <button onClick={() => shareViaSocial('linkedin')} className="share-btn linkedin">
                  LinkedIn
                </button>
              </div>
            </div>

            <div className="how-it-works">
              <h4>How it works:</h4>
              <ol>
                <li>Share your unique referral link with friends</li>
                <li>They sign up using your link and get 50% OFF</li>
                <li>Once they complete registration, you get 10 bonus searches!</li>
                <li>There is no limit - refer as many people as you want!</li>
              </ol>
            </div>
          </div>
        )}

        {activeTab === 'history' && (
          <div className="history-section">
            <h3>Referral History</h3>
            
            {history.length === 0 ? (
              <div className="empty-state">
                <p>No referrals yet</p>
                <p className="empty-description">
                  Start inviting friends to earn bonus searches!
                </p>
              </div>
            ) : (
              <div className="history-table">
                <table>
                  <thead>
                    <tr>
                      <th>Email</th>
                      <th>Status</th>
                      <th>Date Sent</th>
                      <th>Completed</th>
                      <th>Reward</th>
                    </tr>
                  </thead>
                  <tbody>
                    {history.map((referral, index) => (
                      <tr key={index}>
                        <td>{referral.referred_email || referral.referredEmail}</td>
                        <td>
                          <span className={`status-badge ${referral.status}`}>
                            {referral.status === 'completed' ? 'Completed' : 'Pending'}
                          </span>
                        </td>
                        <td>{new Date(referral.created_at || referral.createdAt).toLocaleDateString()}</td>
                        <td>
                          {referral.completed_at || referral.completedAt
                            ? new Date(referral.completed_at || referral.completedAt).toLocaleDateString()
                            : '-'}
                        </td>
                        <td>
                          {referral.status === 'completed' ? (
                            <span className="reward-badge">+10 searches</span>
                          ) : (
                            '-'
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default Referral;